import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { authFetch } from "../utils/authFetch";

const colors = {
  background: "#F7F5F2",
  burgundy: "#681E35",
  dark: "#21171C",
  gold: "#D7B56D",
  muted: "#706970",
};

const euro = (value) =>
  new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: "EUR",
  }).format(Number(value || 0));

const dateLabel = (value) => {
  if (!value) return "Senza scadenza";
  const [year, month, day] = String(value).split("-");
  return day && month && year
    ? `${day}/${month}/${year}`
    : String(value);
};

function benefitLabel(promotion) {
  switch (promotion.benefit_type) {
    case "PERCENT":
      return `-${Number(promotion.discount_value || 0).toLocaleString("it-IT")}%`;
    case "FIXED":
      return `${euro(promotion.discount_value)} di sconto`;
    case "GIFT":
      return "Prodotto omaggio";
    default:
      return "Offerta speciale";
  }
}

function audienceLabel(audience) {
  if (audience === "NEW") return "Riservata ai nuovi clienti";
  if (audience === "SELECTED") return "Offerta dedicata";
  return "Per tutte le aziende";
}

export default function AppB2BPromotionsPage() {
  const navigate = useNavigate();
  const role = (localStorage.getItem("me.role") || "").toUpperCase();
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        const response = await authFetch("/api/b2b/promotions/");
        const data = await response.json();

        if (!response.ok) {
          throw new Error(data?.detail || "Impossibile caricare le promozioni.");
        }

        if (!cancelled) {
          setPromotions(Array.isArray(data) ? data : data.results || []);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Errore di caricamento.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, []);

  return (
    <main style={{ minHeight: "100vh", background: colors.background }}>
      <header style={{
        background: `linear-gradient(135deg, ${colors.dark}, ${colors.burgundy})`,
        padding: "24px 18px 36px",
        color: "#FFFFFF",
      }}>
        <div style={{ maxWidth: 850, margin: "0 auto" }}>
          <button
            type="button"
            onClick={() => navigate("/app")}
            style={{
              background: "transparent",
              color: colors.gold,
              border: 0,
              padding: "0 0 18px",
              cursor: "pointer",
              fontWeight: 700,
            }}
          >
            ← Torna alla home
          </button>

          <div style={{
            color: colors.gold,
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: 2,
            textTransform: "uppercase",
          }}>
            Dallo Zio Roby · Area {role === "AGENTE" ? "Agente" : "Azienda"}
          </div>

          <h1 style={{ fontSize: 29, fontWeight: 800, margin: "10px 0" }}>
            Promozioni aziende
          </h1>

          <p style={{ color: "#EADCE0", marginBottom: 0 }}>
            Scopri le offerte e i vantaggi riservati al mondo business.
          </p>
        </div>
      </header>

      <section style={{
        maxWidth: 850,
        margin: "0 auto",
        padding: "24px 16px 50px",
      }}>
        {loading && (
          <p role="status" style={{ color: colors.muted }}>
            Caricamento promozioni...
          </p>
        )}

        {error && (
          <div role="alert" style={{
            background: "#FFF0F0",
            color: "#9B2434",
            padding: 18,
            borderRadius: 14,
          }}>
            {error}
          </div>
        )}

        {!loading && !error && promotions.length === 0 && (
          <div style={{
            background: "#FFFFFF",
            borderRadius: 18,
            padding: 30,
            textAlign: "center",
            color: colors.muted,
          }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>✦</div>
            <h2 style={{ color: colors.dark, fontSize: 20 }}>
              Nessuna promozione disponibile
            </h2>
            <p style={{ marginBottom: 0 }}>
              Torna a trovarci per scoprire le prossime offerte.
            </p>
          </div>
        )}

        <div style={{ display: "grid", gap: 20 }}>
          {promotions.map((promotion) => (
            <article key={promotion.id} style={{
              background: "#FFFFFF",
              borderRadius: 20,
              overflow: "hidden",
              boxShadow: "0 6px 24px rgba(40,20,30,.08)",
              border: "1px solid #EEE6E3",
            }}>
              {promotion.image_url && (
                <img
                  src={promotion.image_url}
                  alt={promotion.title}
                  style={{
                    width: "100%",
                    height: 220,
                    objectFit: "cover",
                    display: "block",
                  }}
                />
              )}

              <div style={{ padding: 22 }}>
                <div style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 8,
                  marginBottom: 14,
                }}>
                  <span style={{
                    background: colors.burgundy,
                    color: "#FFFFFF",
                    padding: "7px 12px",
                    borderRadius: 30,
                    fontSize: 13,
                    fontWeight: 800,
                  }}>
                    {benefitLabel(promotion)}
                  </span>

                  <span style={{
                    background: "#F7F0E3",
                    color: "#74531E",
                    padding: "7px 12px",
                    borderRadius: 30,
                    fontSize: 12,
                    fontWeight: 700,
                  }}>
                    {audienceLabel(promotion.audience)}
                  </span>
                </div>

                <h2 style={{
                  fontSize: 22,
                  fontWeight: 800,
                  color: colors.dark,
                  marginBottom: 10,
                }}>
                  {promotion.title}
                </h2>

                {promotion.description && (
                  <p style={{
                    color: colors.muted,
                    whiteSpace: "pre-line",
                    lineHeight: 1.6,
                  }}>
                    {promotion.description}
                  </p>
                )}

                {promotion.benefit_type === "GIFT" && (
                  <p style={{ fontWeight: 700, color: colors.burgundy }}>
                    Quantità omaggio: {promotion.gift_quantity}
                  </p>
                )}

                <div style={{
                  borderTop: "1px solid #EEE6E3",
                  paddingTop: 14,
                  marginTop: 16,
                  fontSize: 14,
                  color: colors.muted,
                }}>
                  {Number(promotion.minimum_order || 0) > 0 && (
                    <div>
                      Ordine minimo: <strong>{euro(promotion.minimum_order)}</strong>
                    </div>
                  )}

                  <div>
                    Validità: <strong>
                      {dateLabel(promotion.start_date)} – {dateLabel(promotion.end_date)}
                    </strong>
                  </div>

                  {promotion.max_uses_per_company != null && (
                    <div>
                      Massimo {promotion.max_uses_per_company} utilizzi per azienda
                    </div>
                  )}
                </div>
              </div>
            </article>
          ))}
        </div>

        {!loading && !error && promotions.length > 0 && (
          <p style={{
            color: colors.muted,
            fontSize: 12,
            textAlign: "center",
            marginTop: 24,
          }}>
            Le condizioni delle promozioni saranno verificate
            al momento dell'ordine.
          </p>
        )}
      </section>
    </main>
  );
}
